import {
  Autocomplete,
  Box,
  FormControl,
  InputBase,
  InputLabel,
  alpha,
  createFilterOptions,
  styled,
} from '@mui/material';
import { Country, allCountries } from 'country-telephone-data';

import './phonePrefixFlags.css';

export const ACQUISITION_DEFAULT_PHONE_COUNTRY = 'it';

/** Alphabetical by name with Italy on top, as in the euroansa.it funnel. */
const PHONE_COUNTRIES = [...allCountries].sort((a, b) => {
  if (a.iso2 === ACQUISITION_DEFAULT_PHONE_COUNTRY) return -1;
  if (b.iso2 === ACQUISITION_DEFAULT_PHONE_COUNTRY) return 1;
  return a.name.localeCompare(b.name);
});

const PHONE_COUNTRIES_BY_ISO2 = new Map(
  PHONE_COUNTRIES.map((country) => [country.iso2, country])
);

/**
 * The `+NN` prefix sent to the backend for a selected country. Italy sorts
 * first, so it is also the fallback the select shows for an unknown code: the
 * two must agree, or a restored draft would send the bare `+` the contract
 * refuses while the operator reads `+39` on screen.
 */
export const acquisitionPhonePrefix = (iso2: string) =>
  `+${(PHONE_COUNTRIES_BY_ISO2.get(iso2) ?? PHONE_COUNTRIES[0]).dialCode}`;

// Searchable by country name or dial code, with or without the leading `+`.
const filterCountries = createFilterOptions<Country>({
  stringify: (country) => `${country.name} +${country.dialCode}`,
});

/**
 * Same box as the shared TextInput (white, #5E6366 border, 8px radius,
 * 10px 12px padding), drawn on the root so the flag and the popup arrow
 * sit inside the border.
 */
const PrefixInput = styled(InputBase)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(3),
  },
  backgroundColor: 'white',
  border: '1px solid #5E6366',
  borderRadius: '8px',
  paddingLeft: '12px',
  fontSize: 16,
  transition: theme.transitions.create(['border-color', 'box-shadow']),
  '&.Mui-focused': {
    boxShadow: `${alpha('#8B8D97', 0.25)} 0 0 0 0.2rem`,
    borderColor: '#8B8D97',
  },
  '& .MuiInputBase-input': {
    padding: '10px 12px 10px 8px',
  },
}));

const Flag = ({ iso2 }: { iso2: string }) => (
  <span className={`phone-prefix-flag ${iso2}`} aria-hidden="true" />
);

interface AcquisitionPhonePrefixSelectProps {
  /** Unique per form: two products may be compiled in the same workspace. */
  id: string;
  value: string;
  onChange: (iso2: string) => void;
  onBlur?: () => void;
}

const AcquisitionPhonePrefixSelect = ({
  id,
  value,
  onChange,
  onBlur,
}: AcquisitionPhonePrefixSelectProps) => {
  // Italy sorts first, so it is also the fallback for an unknown code.
  const selected = PHONE_COUNTRIES_BY_ISO2.get(value) ?? PHONE_COUNTRIES[0];

  return (
    <Autocomplete
      id={id}
      options={PHONE_COUNTRIES}
      value={selected}
      onChange={(_event, country) => country && onChange(country.iso2)}
      onBlur={onBlur}
      disableClearable
      autoHighlight
      filterOptions={filterCountries}
      getOptionLabel={(country) => `+${country.dialCode}`}
      isOptionEqualToValue={(option, current) => option.iso2 === current.iso2}
      noOptionsText="Nessun paese"
      slotProps={{
        popper: { style: { width: 320 }, placement: 'bottom-start' },
      }}
      renderOption={({ key, ...props }, country) => (
        <Box component="li" key={key} {...props} sx={{ gap: 1 }}>
          <Flag iso2={country.iso2} />
          {country.name} +{country.dialCode}
        </Box>
      )}
      renderInput={({ InputProps, inputProps, InputLabelProps, id }) => (
        <FormControl variant="standard" fullWidth sx={{ margin: '12px 0' }}>
          <InputLabel
            {...InputLabelProps}
            shrink
            htmlFor={id}
            sx={{
              color: '#5E6366',
              pl: 1,
              '&.Mui-focused': { color: '#5E6366' },
            }}
          >
            Prefisso
          </InputLabel>
          <PrefixInput
            {...InputProps}
            inputProps={inputProps}
            startAdornment={<Flag iso2={selected.iso2} />}
          />
        </FormControl>
      )}
    />
  );
};

export default AcquisitionPhonePrefixSelect;
